// src/components/header/HeaderUsuario.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useLogout from "../../hooks/useLogout";
import useSessionHeartbeat from "../../hooks/useSessionHeartbeat";
import btnLogout2 from "../../assets/img/btnLogout2.svg";
import btnHome2 from "../../assets/img/btnHome2.svg";
import btnLevels6 from "../../assets/img/btnLevels6.svg";
import btnMenu from "../../assets/img/btnMenu.png";
import btnAyuda from "../../assets/img/btnAyuda.svg";

export default function HeaderUsuario({ onOpenChange }) {
  useSessionHeartbeat();

  const [menuExpanded, setMenuExpanded] = useState(false);
  const [user, setUser] = useState(null);
  const logout = useLogout();

  useEffect(() => {
    const userStr = localStorage.getItem("usuario");
    if (userStr) setUser(JSON.parse(userStr));
  }, []);

  // Avisar al padre cuando se abra/cierre
  useEffect(() => {
    onOpenChange?.(menuExpanded);
    return () => onOpenChange?.(false);
  }, [menuExpanded, onOpenChange]);

  const handleLogout = () => logout();

  // Cerrar con ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setMenuExpanded(false);
    };
    if (menuExpanded) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuExpanded]);

  return (
    <div className="relative flex justify-end p-2">
      <div className="flex items-center">
        {/* Botón menú (giro suave al abrir) */}
        <button
          onClick={() => setMenuExpanded((v) => !v)}
          className="focus:outline-none"
          aria-expanded={menuExpanded}
          aria-label="Abrir menú"
        >
          <img
            src={btnMenu}
            alt="Menu"
            className={`w-12 h-auto transition-transform duration-200 ${
              menuExpanded ? "rotate-90" : "rotate-0"
            }`}
          />
        </button>

        {/* --- Escritorio (>= sm): fila hacia la izquierda con stagger --- */}
        {menuExpanded && (
          <div className="hidden sm:flex items-center ml-2 space-x-2 z-50">
            <Link
              to="/ayuda"
              aria-label="Ayuda / Tutorial"
              className="transition-transform duration-200 hover:scale-110 active:scale-95 motion-safe:animate-[slideInRight_200ms_ease-out] [animation-fill-mode:forwards]"
              style={{ animationDelay: "40ms" }}
            >
              <img
                src={btnAyuda}
                alt="Ayuda / Tutorial"
                className="w-12 h-auto"
              />
            </Link>

            <Link
              to="/niveles"
              aria-label="Niveles"
              className="transition-transform duration-200 hover:scale-110 active:scale-95 motion-safe:animate-[slideInRight_220ms_ease-out] [animation-fill-mode:forwards]"
              style={{ animationDelay: "80ms" }}
            >
              <img src={btnLevels6} alt="Niveles" className="w-12 h-auto" />
            </Link>

            {user && user.rol === "Administrador" && (
              <Link
                to="/admin-panel"
                aria-label="Panel de Administración"
                className="transition-transform duration-200 hover:scale-110 active:scale-95 motion-safe:animate-[slideInRight_240ms_ease-out] [animation-fill-mode:forwards]"
                style={{ animationDelay: "120ms" }}
              >
                <img
                  src={btnHome2}
                  alt="Panel de Administración"
                  className="w-12 h-auto"
                />
              </Link>
            )}

            <button
              onClick={handleLogout}
              className="bg-transparent border-none cursor-pointer transition-transform duration-200 hover:scale-110 active:scale-95 motion-safe:animate-[slideInRight_260ms_ease-out] [animation-fill-mode:forwards]"
              aria-label="Cerrar sesión"
              style={{ animationDelay: "160ms" }}
            >
              <img
                src={btnLogout2}
                alt="Cerrar sesión"
                className="w-12 h-auto"
              />
            </button>
          </div>
        )}
      </div>

      {/* --- Móvil (< sm): dropdown hacia abajo con fade+scale --- */}
      {menuExpanded && (
        <>
          {/* Backdrop con fade y blur suave */}
          <div
            className="sm:hidden fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px] motion-safe:animate-[fadeIn_150ms_ease-out]"
            onClick={() => setMenuExpanded(false)}
          />
          {/* Panel dropdown anclado al botón (arriba der), con entrada suave */}
          <div className="sm:hidden absolute right-0 top-[calc(100%+24px)] z-50 w-56">
            <div className="rounded-xl bg-white shadow-lg ring-1 ring-black/5 overflow-hidden origin-top-right motion-safe:animate-[fadeInScale_160ms_ease-out]">
              <nav className="flex flex-col py-1">
                <Link
                  to="/ayuda"
                  onClick={() => setMenuExpanded(false)}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors motion-safe:animate-[slideDown_160ms_ease-out] [animation-fill-mode:forwards]"
                  style={{ animationDelay: "40ms" }}
                >
                  <img src={btnAyuda} alt="" className="w-6 h-auto" />
                  <span className="text-sm font-medium text-gray-800">
                    Ayuda / Tutorial
                  </span>
                </Link>

                <Link
                  to="/niveles"
                  onClick={() => setMenuExpanded(false)}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors motion-safe:animate-[slideDown_180ms_ease-out] [animation-fill-mode:forwards]"
                  style={{ animationDelay: "80ms" }}
                >
                  <img src={btnLevels6} alt="" className="w-6 h-auto" />
                  <span className="text-sm font-medium text-gray-800">
                    Niveles
                  </span>
                </Link>

                {user && user.rol === "Administrador" && (
                  <Link
                    to="/admin-panel"
                    onClick={() => setMenuExpanded(false)}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors motion-safe:animate-[slideDown_200ms_ease-out] [animation-fill-mode:forwards]"
                    style={{ animationDelay: "120ms" }}
                  >
                    <img src={btnHome2} alt="" className="w-6 h-auto" />
                    <span className="text-sm font-medium text-gray-800">
                      Admin Panel
                    </span>
                  </Link>
                )}

                <button
                  onClick={() => {
                    setMenuExpanded(false);
                    handleLogout();
                  }}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left transition-colors motion-safe:animate-[slideDown_220ms_ease-out] [animation-fill-mode:forwards]"
                  style={{ animationDelay: "160ms" }}
                >
                  <img src={btnLogout2} alt="" className="w-6 h-auto" />
                  <span className="text-sm font-medium text-gray-800">
                    Cerrar sesión
                  </span>
                </button>
              </nav>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
